import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  FormField,
  HeadingText,
  Modal,
  ParagraphText,
  Select,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { closeDemoPicker, startDemoChat, useDemoMode } from 'common/demo-mode';
import { fetchDemoKits, type DemoKit } from 'common/demo-kits';
import {
  readDemoSelection,
  writeDemoSelection,
  type DemoSelection,
} from 'common/demo-selection';

// Opened from `Start a demo` in the profile menu: pick the kit and the simulated
// member, then open the chat on that selection.

// The design's 6px corners; GDS's own button radius is a pill, so name the token.
const BUTTON_CSS = { borderRadius: '$medium' };

// The stored selection can point at a kit that no longer exists (it was
// re-cloned, or the tenant re-seeded), or a persona that fell out of a kit's
// cast. Reconcile against what actually loaded rather than let Start demo
// carry a bundle_id/personaId nothing can resolve. Exported for direct
// testing — it is a pure function over the two things that matter, not
// coupled to the effect that calls it.
export const reconcileSelection = (
  previous: DemoSelection,
  loaded: DemoKit[],
): DemoSelection => {
  const kit = loaded.find(({ bundleId }) => bundleId === previous.kitId);
  if (!kit) return { kitId: '', personaId: '' };
  const persona = kit.personas.find(({ id }) => id === previous.personaId);
  return persona ? previous : { ...previous, personaId: '' };
};

export const DemoPickerModal = () => {
  const { formatMessage } = useIntl();
  const { isPickerOpen } = useDemoMode();
  const [selection, setSelection] = useState(readDemoSelection);
  const [kits, setKits] = useState<DemoKit[]>();
  const [failed, setFailed] = useState(false);

  // Loaded on open rather than on mount: a kit is cloned or re-seeded between
  // demos, so a list fetched once would go stale while the app stays up.
  useEffect(() => {
    if (!isPickerOpen) return undefined;

    let current = true;
    setSelection(readDemoSelection());
    setKits(undefined);
    setFailed(false);

    fetchDemoKits()
      .then((loaded) => {
        if (!current) return;
        setKits(loaded);
        // An empty list is as unusable as a failed request: no kit to pick,
        // and Default kit is the only option, so say so the same way.
        setFailed(loaded.length === 0);
        setSelection((previous) => reconcileSelection(previous, loaded));
      })
      .catch(() => {
        if (!current) return;
        setKits([]);
        setFailed(true);
        // No kits loaded here either — same stale-selection risk as above.
        setSelection({ kitId: '', personaId: '' });
      });

    return () => {
      current = false;
    };
  }, [isPickerOpen]);

  const personas =
    kits?.find(({ bundleId }) => bundleId === selection.kitId)?.personas ?? [];

  const handleStartDemo = () => {
    writeDemoSelection(selection);
    startDemoChat();
  };

  return (
    <Modal.Root
      open={isPickerOpen}
      onOpenChange={(open) => {
        if (!open) closeDemoPicker();
      }}
    >
      <Modal.Content
        layout="standard"
        width={{ '@initial': 480 }}
        padding="$oneAndHalf"
        showCloseButton={false}
        // The content area ships as `flex: 1 1 0`, which stretches the fields and
        // pushes the footer past the surface's bottom edge, where `overflow: auto`
        // hides it with no scrollbar to hint at it. Sizing it from its content
        // fixes that. `css` styles the wrapper, so both have to be reached by
        // class, the way the chat does it.
        css={{
          '.GDS-modal': { height: 'auto', maxHeight: '90vh' },
          '.GDS-modal-content': { flex: '0 0 auto' },
        }}
      >
        <Modal.Title>
          <HeadingText level="2" size="md">
            {formatMessage({ id: 'STR_DEMO_PICKER_TITLE' })}
          </HeadingText>
        </Modal.Title>

        <StackLayout
          orientation="vertical"
          spacing="$one"
          css={{ marginBlockStart: '$oneAndHalf', width: '100%' }}
        >
          <FormField
            id="demo-kit"
            label={formatMessage({ id: 'STR_DEMO_PICKER_KIT' })}
            css={{ width: '100%' }}
          >
            {/* FormField clones its id onto this child, so repeating it here
                would be a literal (if harmless) duplicate in the JSX. */}
            <Select
              value={selection.kitId}
              loading={!kits && !failed}
              options={[
                {
                  key: 'none',
                  value: '',
                  label: formatMessage({ id: 'STR_DEMO_PICKER_DEFAULT_KIT' }),
                },
                ...(kits ?? []).map(({ bundleId, name }) => ({
                  key: bundleId,
                  value: bundleId,
                  label: name,
                })),
              ]}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                // The persona belongs to the kit's cast, so it cannot survive a
                // change of kit.
                setSelection({ kitId: event.target.value, personaId: '' })
              }
            />
          </FormField>
          <FormField
            id="demo-persona"
            label={formatMessage({ id: 'STR_DEMO_PICKER_PERSONA' })}
            css={{ width: '100%' }}
          >
            <Select
              value={selection.personaId}
              disabled={personas.length === 0}
              options={[
                {
                  key: 'none',
                  value: '',
                  label: formatMessage({ id: 'STR_DEMO_PICKER_NO_PERSONA' }),
                },
                ...personas.map(({ id, name }) => ({
                  key: id,
                  value: id,
                  label: name,
                })),
              ]}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                setSelection((previous) => ({
                  ...previous,
                  personaId: event.target.value,
                }))
              }
            />
          </FormField>
        </StackLayout>

        {failed && (
          <ParagraphText
            emphasis="subtle"
            size="sm"
            role="status"
            aria-live="polite"
            css={{ marginBlockStart: '$half' }}
          >
            {formatMessage({ id: 'STR_DEMO_PICKER_ERROR' })}
          </ParagraphText>
        )}

        <Box
          css={{
            display: 'flex',
            // The design aligns both buttons with the fields, not to the right.
            justifyContent: 'flex-start',
            gap: '12px',
            marginBlockStart: '$oneAndHalf',
          }}
        >
          <Modal.Close>
            <Button priority="secondary" css={BUTTON_CSS}>
              {formatMessage({ id: 'STR_DEMO_PICKER_CANCEL' })}
            </Button>
          </Modal.Close>
          <Button priority="primary" css={BUTTON_CSS} onClick={handleStartDemo}>
            {formatMessage({ id: 'STR_DEMO_PICKER_CONFIRM' })}
          </Button>
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
};
